import { Injectable } from '@angular/core';
import { AiService } from './ai.service';
import { ApiService } from '../api.service';
import { FileSystemService } from '../file-system.service';
import { Mutex } from '../mutex.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AiFileWriterService {
  constructor(
    private aiService: AiService,
    private api: ApiService,
    private fileSystem: FileSystemService,
    private mutex: Mutex
  ) {
    this.setupAiSubscriptions();
  }

  private setupAiSubscriptions() {
    this.aiService.aiWritingChunk$.subscribe(async (chunk) => {
      if (chunk.section === 'file') {
        this.aiWriteFileChunk(chunk.uri!, chunk.chunk);
        if (chunk.fullContent) {
          const release = await this.mutex.acquire("queue");
          await lastValueFrom(this.api.createFile(chunk.uri!, chunk.fullContent));
          release();
        }
      }

      if (chunk.uri === '/start-new-message') {
        this.fileSystem.refreshRootNode();
      }
    });

    this.aiService.aiWriteSection$.subscribe(async (section) => {
      const release = await this.mutex.acquire();
      if (section.startsWith('file:')) {
        section = section.slice(5);
        const nodeChanged = await this.fileSystem.changeVisibleNode(section, true);
        if (nodeChanged) {
          await this.fileSystem.updateVisibleNode('');
        }
      }
      release();
    });

    this.aiService.refresh$.subscribe(async () => {
      const release = await this.mutex.acquire();
      try {
        await this.fileSystem.refreshRootNode();
      } catch (error) {
        console.error('Error refreshing root node:', error);
      } finally {
        release();
      }
    });
  }

  async aiWriteFileChunk(path: string, delta: string) {
    if (path.startsWith(".")) {
      path = path.slice(1);
      const rootName = this.fileSystem.rootNode.name;
      path = `/${rootName}${path}`;
    }
    
    if (this.fileSystem.visibleNode().path !== path) {
      console.warn("Visible node path does not match ${path}. Changing / Creating ...");
      await this.fileSystem.changeVisibleNode(path, true);
      await this.fileSystem.updateVisibleNode("");
    }
    
    const node = this.fileSystem.visibleNode();
    const currentContent: string = (await node.getContent()) || '';
    await this.fileSystem.updateVisibleNode(currentContent + delta);
  }
}
